import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';

import { axiosGetV3 } from '../../api/axios-handler';
import { useSessionStorage } from '../utils/useSessionStorage';

export const featureFlagKeys = {
  featureFlags: ['featureFlags'] as const,
};

export interface FeatureFlags {
  count: number;
  data: { [key: string]: FeatureFlag };
}

export interface FeatureFlag {
  created_at: Date;
  description: string;
  enabled: boolean;
  id: number;
  key: string;
  client_ids: number[];
}

// NOTE: this is manually synced (AKA copypasta) from app/models/feature_flags.py
export enum Features {
  RECURO_SSO = 'RECURO_SSO',
  PEERSONA = 'PEERSONA',
  FI_CONCIERGE = 'FI_CONCIERGE',
  FOOTER_DISCLAIMER = 'FOOTER_DISCLAIMER',
  SEARCH_MATCH_EXPLAINABILITY = 'SEARCH_MATCH_EXPLAINABILITY',
}

const getFeatureFlags = async (): Promise<FeatureFlags> => {
  return await axiosGetV3(`/feature_flags/me`, {}).then((response: AxiosResponse<FeatureFlags>) => {
    return response.data;
  });
};

export const useFeatureFlag = (key: keyof typeof Features) => {
  const queryClient = useQueryClient();
  const query = useQuery<FeatureFlags>(featureFlagKeys.featureFlags, () => getFeatureFlags(), {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });

  // Memoize the enabled state to prevent unnecessary recalculations
  // Use dataUpdatedAt to track when data actually changes, not just the data reference
  const enabled = useMemo(() => !!query.data?.data[key]?.enabled, [query.dataUpdatedAt, key]);

  // Memoize setEnabled function to prevent creating new function references
  const setEnabled = useMemo(
    () => (newValue: boolean) => {
      queryClient.setQueryData(['featureFlags'], (oldData: FeatureFlags | undefined) => {
        if (!oldData || !oldData.data?.[key]) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            [key]: {
              ...oldData.data[key],
              enabled: newValue,
            },
          },
        };
      });
    },
    [key, queryClient],
  );

  // Memoize the return object, but only recreate when actual query state values change
  // Use dataUpdatedAt as the primary dependency since it only changes when data actually updates
  // This prevents creating new object references on every render
  return useMemo(
    () => ({
      ...query,
      enabled,
      setEnabled,
    }),
    [query, enabled, setEnabled],
  );
};

/* For both flags, if the flag has client_ids, then check if the clientId is in the client_ids array
  If it is, then set the flag to true, if not to false,
  if the flag does not have client_ids, then just check if the flag is enabled, if it is, then set the flag to true, if not to false
*/
/* When client_ids is null/empty: no client restriction, so return the flag's enabled state.
   When client_ids has values: return true only if clientId is in the array. */
function isFlagEnabledForClient(
  enabled: boolean,
  clientIds: number[] | null | undefined,
  clientId: string | null,
): boolean {
  if (!clientId) return enabled;
  if (!clientIds?.length) return enabled; // no client restriction
  return clientIds.includes(Number(clientId));
}

export const usePeersonaFeatureFlagEnabledForClient = () => {
  const clientId = useSessionStorage('clientId');
  const peersonaFeatureFlag = useFeatureFlag(Features.PEERSONA);
  const flag = peersonaFeatureFlag.data?.data[Features.PEERSONA];
  return isFlagEnabledForClient(!!peersonaFeatureFlag.enabled, flag?.client_ids, clientId);
};
